/**
 * ClienteAccordion Component - Groups products by client in expandable sections
 */
import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Building2, Package, Barcode as BarcodeIcon } from 'lucide-react';
import Barcode from 'react-barcode';
import { Product } from '../../services/productService';
import { ClickableBarcode } from './ClickableBarcode';

// Validate barcode - any numeric string with 8+ digits
const isValidBarcode = (value: string): boolean => {
  return /^\d{8,}$/.test(value);
};

interface ClienteGroup {
  clienteId: number | null;
  clienteNome: string;
  products: Product[];
}

interface ClienteAccordionProps {
  products: Product[];
}

export const ClienteAccordion: React.FC<ClienteAccordionProps> = ({
  products,
}) => {
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  // Group products by client
  const clienteGroups: ClienteGroup[] = useMemo(() => {
    const groups = new Map<string, ClienteGroup>();

    products.forEach((product) => {
      const key = product.cliente_id?.toString() || 'sem-cliente';
      const clienteNome = product.cliente_nome_fantasia || product.cliente_razao_social || 'Sem Cliente';

      if (!groups.has(key)) {
        groups.set(key, {
          clienteId: product.cliente_id || null,
          clienteNome,
          products: [],
        });
      }
      groups.get(key)!.products.push(product);
    });

    // Sort by client name
    return Array.from(groups.values()).sort((a, b) =>
      a.clienteNome.localeCompare(b.clienteNome)
    );
  }, [products]);

  const toggleClient = (clienteKey: string) => {
    setExpandedClients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clienteKey)) {
        newSet.delete(clienteKey);
      } else {
        newSet.add(clienteKey);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allKeys = clienteGroups.map((g) => g.clienteId?.toString() || 'sem-cliente');
    setExpandedClients(new Set(allKeys));
  };

  const collapseAll = () => {
    setExpandedClients(new Set());
  };

  if (products.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Nenhum produto cadastrado no sistema.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with expand/collapse controls */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-semibold">{clienteGroups.length}</span> clientes |{' '}
          <span className="font-semibold">{products.length}</span> produtos
        </div>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors"
          >
            Expandir Todos
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Recolher Todos
          </button>
        </div>
      </div>

      {/* Accordion list */}
      <div className="space-y-2">
        {clienteGroups.map((group) => {
          const clienteKey = group.clienteId?.toString() || 'sem-cliente';
          const isExpanded = expandedClients.has(clienteKey);

          return (
            <div
              key={clienteKey}
              className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden"
            >
              {/* Accordion Header */}
              <button
                onClick={() => toggleClient(clienteKey)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-gray-900">{group.clienteNome}</span>
                  <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full">
                    {group.products.length} produtos
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </button>

              {/* Accordion Content */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Produto
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Código
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            GTIN/EAN
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Código de Barras
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.products.map((product) => {
                          const barcodeValue =
                            product.gtin || product.ean || '';  // GTIN-S-002: sem GTIN = vazio
                          const hasValidBarcode = isValidBarcode(barcodeValue);

                          return (
                            <tr key={product.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {product.nome}
                                </div>
                                {product.descricao && (
                                  <div className="text-xs text-gray-500 truncate max-w-xs">
                                    {product.descricao}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-mono text-gray-700">
                                  {product.codigo}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-mono text-gray-700">
                                  {product.gtin || product.ean || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {hasValidBarcode ? (
                                  <ClickableBarcode productName={product.nome}>
                                    <div className="bg-white p-1 rounded border border-gray-100 inline-block">
                                      <Barcode
                                        value={barcodeValue}
                                        format="CODE128"
                                        width={1}
                                        height={30}
                                        displayValue={true}
                                        fontSize={9}
                                        margin={2}
                                      />
                                    </div>
                                  </ClickableBarcode>
                                ) : (
                                  <div className="flex items-center gap-1 text-yellow-600 text-xs">
                                    <BarcodeIcon className="w-4 h-4" />
                                    <span>Indisponível</span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
